import { writeStorage, deleteFromStorage, isTypeOfLocalStorageChanged, LOCAL_STORAGE_CHANGE_EVENT_NAME, readFromStorage, } from './local-storage-events';
import { storage } from './storage';
import { useEffect, useState, useCallback } from 'react';
import { tryParse } from './utils';
import { useIsBrowser } from '../../hooks';
export function useLocalStorage(key, defaultValue = null) {
    const [localState, updateLocalState] = useState(readFromStorage(key, defaultValue));
    const isBrowser = useIsBrowser();
    const onLocalStorageChange = useCallback((event) => {
        // An event value can be of TValue when `localStorage.setItem` is called, or null when
        // `localStorage.removeItem` is called.
        if (isTypeOfLocalStorageChanged(event)) {
            if (event.detail.key === key) {
                updateLocalState(event.detail.value);
            }
        }
        else {
            if (event.key === key) {
                updateLocalState(event.newValue === null ? null : tryParse(event.newValue));
            }
        }
    }, [updateLocalState, key]);
    useEffect(() => {
        if (!isBrowser) {
            return;
        }
        // The custom storage event allows us to update our component
        // when a change occurs in localStorage outside our component
        const listener = (e) => {
            onLocalStorageChange(e);
        };
        window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT_NAME, listener);
        // The storage event only works in the context of other documents (eg. other browser tabs)
        window.addEventListener('storage', listener);
        // Write default value to the local storage if there currently isn't any value there.
        // Don't however write a defaultValue that is null otherwise it'll trigger infinite updates.
        if (storage.getItem(key) === null && defaultValue !== null) {
            writeStorage(key, defaultValue);
        }
        return () => {
            window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT_NAME, listener);
            window.removeEventListener('storage', listener);
        };
    }, [key, defaultValue, onLocalStorageChange, isBrowser]);
    const writeState = useCallback((value) => writeStorage(key, value), [key]);
    const deleteState = useCallback(() => deleteFromStorage(key), [key]);
    const state = localState ?? defaultValue;
    return [state, writeState, deleteState];
}
